import {
  type BoxStaffDetailedDto,
  type BoxStaffItemDto,
  BoxStaffRole,
  BoxStaffStatus,
} from "@modules/boxStaff/gateway/boxStaffDto"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import { PublicProfileEntity } from "@modules/profile/publicProfile/entities/publicProfileEntity"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import { type HUImage } from "@modules/types/imageTypes"
export type BoxStaffItemState = Omit<
  BoxStaffItemEntity,
  "convertToUser" | "getUserName" | "getFullName" | "getInitials"
>
export class BoxStaffItemEntity {
  id: string
  userId: string
  boxId: string
  role: BoxStaffRole
  status: BoxStaffStatus
  firstname: string
  lastname: string
  email: string
  avatar?: HUImage
  creationDate: Date
  username: string
  internalNote?: string
  archived: boolean
  archivedDate?: Date

  private constructor(boxStaffItemEntity: BoxStaffItemState) {
    this.id = boxStaffItemEntity.id
    this.userId = boxStaffItemEntity.userId
    this.boxId = boxStaffItemEntity.boxId
    this.role = boxStaffItemEntity.role
    this.status = boxStaffItemEntity.status
    this.firstname = boxStaffItemEntity.firstname
    this.lastname = boxStaffItemEntity.lastname
    this.email = boxStaffItemEntity.email
    this.avatar = boxStaffItemEntity.avatar
    this.creationDate = boxStaffItemEntity.creationDate
    this.username = boxStaffItemEntity.username
    this.internalNote = boxStaffItemEntity.internalNote
    this.archived = boxStaffItemEntity.archived
    this.archivedDate = boxStaffItemEntity.archivedDate
  }

  static fromItemDto(dto: BoxStaffItemDto): BoxStaffItemEntity {
    return new BoxStaffItemEntity({
      id: dto.id,
      userId: dto.userId,
      boxId: dto.boxId,
      role: dto.role,
      status: dto.status,
      firstname: dto.firstname ?? "",
      lastname: dto.lastname ?? "",
      email: dto.email,
      avatar: dto.avatar ? convertImageFromDto(dto.avatar) : undefined,
      creationDate: new Date(dto.creationDate),
      username: dto.username,
      internalNote: dto.internalNote ?? "",
      archived: dto.archived,
      archivedDate: dto.archivedDate ? new Date(dto.archivedDate) : undefined,
    })
  }

  getUserName(): string {
    if (this.username === "") {
      return this.username
    }
    return "@" + this.username
  }

  getFullName(): string {
    return `${this.firstname} ${this.lastname}`
  }

  getInitials(): string {
    return `${this.firstname.charAt(0)}${this.lastname.charAt(0)}`.toUpperCase()
  }

  convertToUser(): UserEntity {
    return {
      id: this.userId,
      firstname: this.firstname,
      lastname: this.lastname,
      username: this.username,
      avatar: this.avatar ?? null,
    }
  }
}

export class BoxStaffDetailedEntity {
  id: string
  userId: string
  boxId: string
  role: BoxStaffRole
  status: BoxStaffStatus
  firstname: string
  lastname: string
  email: string
  avatar?: HUImage
  creationDate: Date
  username: string
  internalNote?: string
  archived: boolean
  publicProfile?: PublicProfileEntity

  private constructor(boxStaffDetailed: BoxStaffDetailedEntity) {
    this.id = boxStaffDetailed.id
    this.userId = boxStaffDetailed.userId
    this.boxId = boxStaffDetailed.boxId
    this.role = boxStaffDetailed.role
    this.status = boxStaffDetailed.status
    this.firstname = boxStaffDetailed.firstname
    this.lastname = boxStaffDetailed.lastname
    this.email = boxStaffDetailed.email
    this.avatar = boxStaffDetailed.avatar
    this.creationDate = boxStaffDetailed.creationDate
    this.username = boxStaffDetailed.username
    this.internalNote = boxStaffDetailed.internalNote
    this.archived = boxStaffDetailed.archived
    this.publicProfile = boxStaffDetailed.publicProfile
  }

  static fromDetailedDto(dto: BoxStaffDetailedDto): BoxStaffDetailedEntity {
    return new BoxStaffDetailedEntity({
      id: dto.id,
      userId: dto.userId,
      boxId: dto.boxId,
      role: dto.role,
      status: dto.status,
      firstname: dto.firstname ?? "",
      lastname: dto.lastname ?? "",
      email: dto.email,
      avatar: dto.avatar ? convertImageFromDto(dto.avatar) : undefined,
      creationDate: new Date(dto.creationDate),
      username: dto.username,
      internalNote: dto.internalNote ?? "",
      archived: dto.archived,
      publicProfile: dto.publicProfile ? PublicProfileEntity.fromDto(dto.publicProfile) : undefined,
    })
  }
}
